import React from 'react';
/**
 * DjayAvatar — full body composition for the non-`hideBody` render.
 *
 * Owns the EXACT draw order of the original `DjayAvatar` SVG body:
 *   shadow → tail → arms → body(+variant sparkles) → flame → tier → glasses
 *   → eyes → mouth → cheeks → feet
 * Re-ordering would change z-stacking, so this sequence is intentionally kept
 * in one place. Per-feature shapes live in their own sibling part-files.
 */
import type { DjayEmotion, DjayTier, DjayVariant, VariantColorScheme } from '../types';
import { Arms } from './Arms';
import { Eyes } from './Eyes';
import { Mouth } from './Mouth';
import { TierElements } from './TierElements';
interface BodyLayersProps {
  uid: string;
  colors: VariantColorScheme;
  emotion: DjayEmotion;
  tier: DjayTier;
  variant: DjayVariant;
  showTail: boolean;
  showDetails: boolean;
  showMouth: boolean;
  showFullArms: boolean;
  showArmStubs: boolean;
}
export function BodyLayers({
  uid,
  colors,
  emotion,
  tier,
  variant,
  showTail,
  showDetails,
  showMouth,
  showFullArms,
  showArmStubs
}: BodyLayersProps) {
  return <>
      <g className="djay-shadow" data-id="element-3112">
        <ellipse cx="100" cy="205" rx="48" ry="10" fill="#1E293B" fillOpacity="0.12" data-id="element-3113" />
      </g>
      {showTail && <g className="djay-tail" data-id="element-3114">
          <path d="M152 150 C162 142, 176 140, 182 152 C188 164, 178 178, 168 176 C158 174, 166 160, 176 158 C182 156, 180 168, 174 172" stroke={colors.tailStroke} strokeWidth="10" strokeLinecap="round" fill="none" data-id="element-3115" />
          <path d="M152 150 C162 142, 176 140, 182 152 C188 164, 178 178, 168 176 C158 174, 166 160, 176 158 C182 156, 180 168, 174 172" stroke={colors.armColor} strokeWidth="7" strokeLinecap="round" fill="none" data-id="element-3116" />
          <path d="M154 148 C162 141, 174 140, 180 150" stroke={colors.tailHighlight} strokeWidth="3" strokeLinecap="round" fill="none" data-id="element-3117" />
          <circle cx="174" cy="171" r="4" fill={colors.armColor} data-id="element-3118" />
          <circle cx="174" cy="171" r="2.5" fill={colors.tailHighlight} data-id="element-3119" />
        </g>}
      <Arms emotion={emotion} colors={colors} showFullArms={showFullArms} showArmStubs={showArmStubs} data-id="element-3120" />
      <g className="djay-body" data-id="element-3121">
        <path d="M100 195 C55 195, 35 155, 35 115 C35 65, 60 45, 100 45 C140 45, 165 65, 165 115 C165 155, 145 195, 100 195 Z" fill={`url(#${uid}-body-grad)`} data-id="element-3122" />
        <path d="M68 78 C52 95, 48 125, 53 150 C48 125, 52 88, 73 73 Z" fill={colors.tailHighlight} fillOpacity="0.5" data-id="element-3123" />
        <path d="M146 98 C156 120, 156 152, 146 178 C162 152, 162 115, 146 93 Z" fill={colors.feetDark} fillOpacity="0.3" data-id="element-3124" />
        {showDetails && <ellipse cx="100" cy="155" rx="25" ry="18" fill={colors.tailHighlight} fillOpacity="0.25" data-id="element-3125" />}
        {variant === 'cosmic' && <g className="djay-cosmic-stars" data-id="element-3126">
            <circle cx="60" cy="100" r="1.5" fill="white" fillOpacity="0.8" data-id="element-3127" />
            <circle cx="140" cy="140" r="1.5" fill="white" fillOpacity="0.6" data-id="element-3128" />
            <circle cx="80" cy="160" r="2" fill="white" fillOpacity="0.7" data-id="element-3129" />
            <circle cx="120" cy="70" r="1" fill="white" fillOpacity="0.9" data-id="element-3130" />
            <circle cx="50" cy="130" r="1" fill="white" fillOpacity="0.5" data-id="element-3131" />
          </g>}
        {variant === 'rainbow' && <g className="djay-rainbow-sparkles" data-id="element-3132">
            <circle cx="62" cy="100" r="2" fill="#FDE047" fillOpacity="0.85" data-id="element-3133" />
            <circle cx="138" cy="140" r="2" fill="#F472B6" fillOpacity="0.85" data-id="element-3134" />
            <circle cx="80" cy="165" r="2.5" fill="#A78BFA" fillOpacity="0.85" data-id="element-3135" />
            <circle cx="125" cy="75" r="1.5" fill="#34D399" fillOpacity="0.9" data-id="element-3136" />
            <circle cx="55" cy="135" r="1.5" fill="#60A5FA" fillOpacity="0.85" data-id="element-3137" />
            <circle cx="145" cy="105" r="1.5" fill="#FB923C" fillOpacity="0.85" data-id="element-3138" />
          </g>}
      </g>
      <g className="djay-flame" data-id="element-3139">
        <path d="M100 52 C88 46, 83 34, 86 18 C90 8, 100 2, 100 2 C100 2, 110 8, 114 18 C117 34, 112 46, 100 52 Z" fill={colors.flameColors[0]} data-id="element-3140" />
        <path d="M100 50 C89 45, 85 34, 88 20 C91 10, 100 5, 100 5 C100 5, 109 10, 112 20 C115 34, 111 45, 100 50 Z" fill={colors.flameColors[1]} data-id="element-3141" />
        <path d="M100 48 C93 44, 90 35, 92 24 C95 15, 100 11, 100 11 C100 11, 105 15, 108 24 C110 35, 107 44, 100 48 Z" fill={colors.flameColors[2]} data-id="element-3142" />
        <path d="M97 40 C94 36, 93 29, 95 23 C96 19, 98 17, 98 17 C98 17, 96 24, 97 31 C97 35, 97 38, 97 40 Z" fill={colors.flameColors[3]} data-id="element-3143" />
      </g>
      <TierElements tier={tier} uid={uid} showDetails={showDetails} data-id="element-3144" />
      <g className="djay-glasses" data-id="element-3145">
        <path d="M90 92 C96 87, 104 87, 110 92" stroke={colors.glassesFrame} strokeWidth="4" strokeLinecap="round" fill="none" data-id="element-3146" />
        <rect x="49" y="82" width="42" height="36" rx="7" ry="7" fill="none" stroke={colors.glassesFrame} strokeWidth="5" data-id="element-3147" />
        <rect x="52" y="85" width="36" height="30" rx="5" ry="5" fill={colors.glassesLens} data-id="element-3148" />
        <rect x="55" y="87" width="12" height="6" rx="3" ry="3" fill="white" fillOpacity="0.55" data-id="element-3149" />
        <rect x="109" y="82" width="42" height="36" rx="7" ry="7" fill="none" stroke={colors.glassesFrame} strokeWidth="5" data-id="element-3150" />
        <rect x="112" y="85" width="36" height="30" rx="5" ry="5" fill={colors.glassesLens} data-id="element-3151" />
        <rect x="115" y="87" width="12" height="6" rx="3" ry="3" fill="white" fillOpacity="0.55" data-id="element-3152" />
        <path d="M49 96 L40 94" stroke={colors.glassesFrame} strokeWidth="4" strokeLinecap="round" data-id="element-3153" />
        <path d="M151 96 L160 94" stroke={colors.glassesFrame} strokeWidth="4" strokeLinecap="round" data-id="element-3154" />
      </g>
      <Eyes emotion={emotion} data-id="element-3155" />
      <Mouth emotion={emotion} colors={colors} showMouth={showMouth} showDetails={showDetails} data-id="element-3156" />
      {showDetails && <g className="djay-cheeks" data-id="element-3157">
          <ellipse cx="52" cy="120" rx="9" ry="5" fill="#FDBA74" fillOpacity="0.5" data-id="element-3158" />
          <ellipse cx="148" cy="120" rx="9" ry="5" fill="#FDBA74" fillOpacity="0.5" data-id="element-3159" />
        </g>}
      {showFullArms && <g className="djay-feet" data-id="element-3160">
          <ellipse cx="80" cy="196" rx="14" ry="6" fill={colors.feetDark} data-id="element-3161" />
          <ellipse cx="80" cy="195" rx="14" ry="6" fill={colors.feetLight} data-id="element-3162" />
          <ellipse cx="120" cy="196" rx="14" ry="6" fill={colors.feetDark} data-id="element-3163" />
          <ellipse cx="120" cy="195" rx="14" ry="6" fill={colors.feetLight} data-id="element-3164" />
        </g>}
    </>;
}